import { z } from 'zod';

const EMAIL_LIST_SEPARATOR = ',';
const EMAIL_LIST_JOINER = ', ';

const emailSchema = z.string().email();
/** Limits the backend email check (validator.js) adds to the format */
const EMAIL_LOCAL_PART_MAX_LENGTH = 64;
const EMAIL_DOMAIN_LABEL_MAX_LENGTH = 63;

export const splitEmailList = (value: string): string[] =>
  value.trim() === '' ? [] : value.split(EMAIL_LIST_SEPARATOR).map(email => email.trim());

export const joinEmailList = (emails: string[]): string => emails.join(EMAIL_LIST_JOINER);

export const normalizeEmailList = (value: string): string =>
  joinEmailList([...new Set(splitEmailList(value).filter(email => email.length > 0))]);

const isValidDomainLabel = (label: string): boolean =>
  label.length <= EMAIL_DOMAIN_LABEL_MAX_LENGTH && !label.endsWith('-');

export const isValidEmail = (email: string): boolean => {
  if (!emailSchema.safeParse(email).success) return false;
  const [localPart, domain] = email.split('@');
  return (
    localPart.length <= EMAIL_LOCAL_PART_MAX_LENGTH && domain.split('.').every(isValidDomainLabel)
  );
};

export const isValidEmailList = (value: string): boolean =>
  splitEmailList(value).every(isValidEmail);

interface GetEmailListSchemaParams {
  maxLength: number;
  /** Translated text or an i18n key; `FormMessage` translates keys */
  maxLengthMessage: string;
  invalidMessage: string;
}

/** Mirrors the backend `IsEmailListConstraint`. */
export const getEmailListSchema = ({
  maxLength,
  maxLengthMessage,
  invalidMessage,
}: GetEmailListSchemaParams) =>
  z.string().max(maxLength, maxLengthMessage).refine(isValidEmailList, invalidMessage);
